<template>
    <div class="module-time-tracker">
        <template v-if="!data">
            <loading-indicator/>
        </template>
        <template v-else>
            <form-row label="Mitarbeiter">
                <div class="time-tracker-reason-box-bold">{{getUsername(data.employee_id, 'employee')}}</div>
            </form-row>

            <form-row label="Beschreibung">
                <div class="time-tracker-reason-box-bold">{{data.description}}</div>
            </form-row>

            <div class="row">
                <div class="col-6">
                    <form-row label="Woche">
                        <div class="time-tracker-reason-box-bold">{{data.week_range_label}}</div>
                    </form-row>
                </div>

                <div class="col-6">
                    <form-row label="Beantragt">
                        <div class="time-tracker-reason-box-bold">{{data.requested_hours}} Stunden ({{data.type}})</div>
                    </form-row>
                </div>
            </div>

            <template v-if="data.dismantling_coretime">
                <template v-if="representationWarning.colliding_absences && representationWarning.colliding_absences.length">
                    <div class="info">
                        <i class="material-icons md-light" aria-hidden="true" role="img">info</i>
                        <div>
                            An folgenden Tagen liegen Kollisionen mit den Vertretungsreglungen aufgrund abwesender Mitarbeiter vor:
                            <ul>
                                <li v-for="absence of representationWarning.colliding_absences">{{absence}}</li>
                            </ul>
                        </div>
                    </div>
                </template>

                <template v-if="representationWarning.invalid_regulations && representationWarning.invalid_regulations.length">
                    <div class="info">
                        <i class="material-icons md-light" aria-hidden="true" role="img">info</i>
                        <div>
                            Die Anzahl benötigter Mitarbeiter gleicht oder übersteigt die der Mitarbeiter in der Vertretungsregelung! Bitte überprüfen Sie die folgenden Vertretungsregelungen:
                            <ul>
                                <li v-for="regulation of representationWarning.invalid_regulations">{{regulation.title}}</li>
                            </ul>
                        </div>
                    </div>
                </template>

                <template v-if="representationWarning.colliding_regulations && representationWarning.colliding_regulations.length">
                    <template v-for="regulation of representationWarning.colliding_regulations">
                        <h2>{{regulation.title}}</h2>
                        <absence-calendar :year="currentYear" :regulation-id="regulation.id" :ovr-id="data.id" :display-navigation="false" tag="div"/>
                    </template>
                </template>

                <form-row label="Abwesenheiten">
                    <div class="time-tracker-reason-box-bold">
                        <template v-for="day of weekDays">
                            <template v-if="data.absences[`${day.key}_absence`] === 1">
                                {{day.name}}: {{data.absences[`${day.key}_absence_all_day`] ? 'Ganztägig' : data.absences[`${day.key}_absence_from`] + ' - ' + data.absences[`${day.key}_absence_to`]}}<br>
                            </template>
                        </template>
                    </div>
                </form-row>
            </template>

            <template v-if="0 === data.state">
                <form-row>
                    <div class="time-tracker-overtime-box" :style="{color: remainingOvertimeColor}">
                        Arbeitszeitkonto: {{remainingOvertime}}
                    </div>
                </form-row>
                <form-row>
                    <div class="time-tracker-overtime-box" :style="{color: vacationUnusedDaysColor}">
                        Resturlaub: {{vacationUnusedDays}} Tag{{1 !== vacationUnusedDays ? 'e' : ''}}
                    </div>
                </form-row>
            </template>

            <form-row label="Status">
                <div v-if="0 === data.state" class="time-tracker-reason-box-bold"><i class="material-icons" aria-hidden="true" role="img">hourglass_empty</i> Beantragt</div>
                <div v-else-if="1 === data.state" class="time-tracker-reason-box-bold"><i class="material-icons text-success" aria-hidden="true" role="img">check</i> Antrag angenommen</div>
                <div v-else-if="2 === data.state" class="time-tracker-reason-box-bold"><i class="material-icons text-danger" aria-hidden="true" role="img">clear</i> Antrag abgelehnt</div>
                <div v-else-if="3 === data.state" class="time-tracker-reason-box-bold"><i class="material-icons text-warning" aria-hidden="true" role="img">undo</i> Antrag zurückgezogen</div>
                <div v-else-if="4 === data.state" class="time-tracker-reason-box-bold"><i class="material-icons text-warning" aria-hidden="true" role="img">hourglass_empty</i> Änderung ausstehend</div>
            </form-row>

            <form-row v-if="data.message" label="Begründung">
                <div class="time-tracker-reason-box-bold">{{data.message}}</div>
            </form-row>

            <form-row label="Erstellt">
                <div class="time-tracker-reason-box-bold">{{dateHelper('d.m.Y H:i:s', data.created)}}</div>
            </form-row>

            <form-row v-if="data.editor" label="Bearbeitet">
                <div class="time-tracker-reason-box-bold">{{dateHelper('d.m.Y H:i:s', data.edited)}} von {{getUsername(data.editor)}}</div>
            </form-row>
        </template>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator';
    import FormRow from '../../../components/form/FormRow.vue';
    import LoadingIndicator from '../../../components/LoadingIndicator.vue';
    import {getUsername} from '../../../filters/username';
    import {dayNames, dayNamesEn, weekDays} from '../../../library/date';
    import TopMessageHandler from '../../../library/topMessageHandler';
    import dateHelper from '../../../utils/portuna/dateHelper';
    import AbsenceCalendar from '../../absences/AbsenceCalendar.vue';

    @Component({
        name: 'RequestViewPopup',
        components: {
            AbsenceCalendar,
            LoadingIndicator,
            FormRow
        },
        methods: {
            getUsername,
            dateHelper,
        }
    })
    export default class RequestViewPopup extends Vue
    {
        @Prop({
            type: Number,
            default: 0,
            required: false,
        }) id: number;

        @Prop({
            type: Number,
            default: 0,
            required: false,
        }) absenceId: number;

        currentYear: number = dateHelper('Y') as number;

        data = null as Nullable<OvertimeRequestListEntry>;

        representationWarning: VacationRegulationWarning = {};

        remainingOvertime: string = '';

        remainingOvertimeColor: string = '';

        vacationUnusedDays: number = 0;

        weekDays = weekDays;

        get vacationUnusedDaysColor()
        {
            if(this.vacationUnusedDays < 0)
            {
                return '#b4171b';
            }

            if(this.vacationUnusedDays > 0)
            {
                return '#26a65b';
            }

            return '';
        }

        created()
        {
            this.fetch();
        }

        fetch()
        {
            this.axios.get(`/employee/time-tracker/overtime/get-request-template?id=${this.id}&absence-id=${this.absenceId}`)
            .then(({data: {data: r}}) => {
                this.data = r.data;
                this.representationWarning = r.representationWarning;
                this.remainingOvertime = r.remainingOvertime;
                this.remainingOvertimeColor = r.remainingOvertimeColor;
                this.vacationUnusedDays = r.vacation_unused_days;
            })
            .catch((err) => {
                TopMessageHandler.error('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später noch einmal.');
                console.error(err);
                this.emitter.emit('close');
            })
        }

        getDayName(day: string)
        {
            return dayNames[dayNamesEn.indexOf(day.charAt(0).toUpperCase() + day.slice(1))];
        }
    }
</script>